import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import * as state from '../../_state/organizationDialog.state';
import { useTranslations } from '../../i18n/useTranslations';

import { SelectTimeZone } from '~/shared/components/select-fields/SelectTimeZone';

export const OrganizationDetailsDialog = () => {
  const t = useTranslations();
  const toast = useToast();

  const [isOpen, setIsOpen] = useAtom(state.$organizationDialogOpen);
  const mode = useAtomValue(state.$organizationDialogMode);
  const formData = useAtomValue(state.$organizationFormData);
  const isFormValid = useAtomValue(state.$organizationFormValid);
  const isSaving = useAtomValue(state.$organizationDialogSaving);

  const updateFormData = useSetAtom(state.$updateOrganizationFormData);
  const saveOrganization = useSetAtom(state.$saveOrganization);
  const closeDialog = useSetAtom(state.$closeOrganizationDialog);

  const handleSave = React.useCallback(async () => {
    try {
      await saveOrganization();
      toast.success(
        mode === 'create'
          ? t.organizationDetailsDialog.saveSuccess.create
          : t.organizationDetailsDialog.saveSuccess.update,
      );
    } catch (_error) {
      toast.error(t.organizationDetailsDialog.saveError);
    }
  }, [saveOrganization, mode, toast, t]);

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={() => setIsOpen(false)}
      className="@OrganizationDetailsDialog"
    >
      <DialogContent className="min-w-[500px]">
        <DialogHeader slot="title">
          <DialogTitle>
            {mode === 'create'
              ? t.organizationDetailsDialog.title.create
              : t.organizationDetailsDialog.title.edit}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 p-4">
          <TextField
            aria-label={t.organizationDetailsDialog.fields.name.label}
            label={t.organizationDetailsDialog.fields.name.label}
            placeholder={t.organizationDetailsDialog.fields.name.placeholder}
            value={formData.name}
            onChange={(value) => updateFormData({ name: value })}
            isRequired
            autoFocus
          />

          <TextField
            aria-label={t.organizationDetailsDialog.fields.domain.label}
            label={t.organizationDetailsDialog.fields.domain.label}
            placeholder={t.organizationDetailsDialog.fields.domain.placeholder}
            value={formData.domain}
            onChange={(value) => updateFormData({ domain: value })}
            isRequired
            description={t.organizationDetailsDialog.fields.domain.description}
          />

          <SelectTimeZone
            label={t.organizationDetailsDialog.fields.timezone.label}
            placeholder={
              t.organizationDetailsDialog.fields.timezone.placeholder
            }
            value={formData.timezone}
            onChange={(value) => updateFormData({ timezone: value })}
          />

          <TextField
            aria-label={t.organizationDetailsDialog.fields.idpId.label}
            label={t.organizationDetailsDialog.fields.idpId.label}
            placeholder={t.organizationDetailsDialog.fields.idpId.placeholder}
            value={formData.idpId ?? ''}
            onChange={(value) => updateFormData({ idpId: value || undefined })}
            description={t.organizationDetailsDialog.fields.idpId.description}
          />
        </div>

        <DialogFooter>
          <Button
            aria-label={t.organizationDetailsDialog.actions.cancel}
            variant="outline"
            color="neutral"
            onPress={closeDialog}
            isDisabled={isSaving}
          >
            {t.organizationDetailsDialog.actions.cancel}
          </Button>
          <Button
            aria-label={
              mode === 'create'
                ? t.organizationDetailsDialog.actions.create
                : t.organizationDetailsDialog.actions.save
            }
            variant="solid"
            color="primary"
            onPress={handleSave}
            isDisabled={!isFormValid || isSaving}
            isLoading={isSaving}
          >
            {mode === 'create'
              ? t.organizationDetailsDialog.actions.create
              : t.organizationDetailsDialog.actions.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
